import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { authApi } from "@/api/auth";

interface TOTPManagementProps {
  enabled: boolean;
  onDisable: () => void;
}

export const TOTPManagement = ({
  enabled,
  onDisable,
}: TOTPManagementProps) => {
  const [showDisableDialog, setShowDisableDialog] = useState(false);

  const handleDisable2FA = async () => {
    try {
      const response = await authApi.disableTOTP();

      if (response.code === 200 || response.success) {
        onDisable();
        setShowDisableDialog(false);
        toast.success("Two-factor authentication has been disabled");
      } else {
        toast.error(response.message || response.msg || "Failed to disable 2FA");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to disable 2FA");
    }
  };

  return (
    <>
      <Card className="glass-card p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {enabled ? (
              <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-green-500" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <ShieldOff className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold">Two-Factor Authentication</h3>
              <p className="text-sm text-muted-foreground">
                {enabled
                  ? "Your account is protected with 2FA"
                  : "Add an extra layer of security to your account"}
              </p>
            </div>
          </div>
          <Badge variant={enabled ? "default" : "secondary"}>
            {enabled ? "Enabled" : "Disabled"}
          </Badge>
        </div>

        {enabled && (
          <Button
            variant="destructive"
            onClick={() => setShowDisableDialog(true)}
          >
            Disable 2FA
          </Button>
        )}
      </Card>

      <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <AlertDialogContent className="glass-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Two-Factor Authentication?</AlertDialogTitle>
            <AlertDialogDescription>
              This will make your account less secure. You will only need your password to log in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisable2FA} className="bg-destructive hover:bg-destructive/90">
              Disable Two Factor Authentication
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
